'use client';

import { useState } from 'react';
import {
  Typography,
  Stack,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Box,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import type { UserMachineWithDetails } from 'lib/machines';
import { formatMeasurementsMm } from 'lib/measurements/formatMeasurement';

interface MachinesSectionProps {
  machines: UserMachineWithDetails[];
  isOwnProfile: boolean;
  userId: string;
}

export default function MachinesSection({ machines, isOwnProfile, userId }: MachinesSectionProps) {
  const router = useRouter();
  const [selectedMachine, setSelectedMachine] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleAddMachine = () => {
    router.push(`/machines/select?userId=${userId}`);
  };

  const handleDeleteMachine = async () => {
    if (!selectedMachine) return;

    try {
      const response = await fetch(`/api/users/${userId}/machines?machineId=${selectedMachine}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete machine');
      }

      router.refresh();
      setShowDeleteDialog(false);
      setSelectedMachine(null);
    } catch (error) {
      console.error('Error deleting machine:', error);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Typography variant="h6">Machines</Typography>
        {isOwnProfile && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddMachine}>
            Add Machine
          </Button>
        )}
      </Stack>

      {machines.length === 0 ? (
        <Typography color="text.secondary">No machines added yet.</Typography>
      ) : (
        <List>
          {machines.map(({ machine }) => (
            <ListItem key={machine.id} component={Card} sx={{ mb: 1, p: 0 }}>
              <Stack direction="row" sx={{ width: '100%' }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 160,
                    aspectRatio: '1',
                    borderRadius: '4px 0 0 4px',
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    flexShrink: 0,
                  }}
                >
                  {machine.pictureUrl ? (
                    <Image
                      src={machine.pictureUrl}
                      alt={machine.name}
                      fill
                      sizes="160px"
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" align="center">
                        No image
                      </Typography>
                    </Box>
                  )}
                </Box>
                <CardContent sx={{ flex: 1, p: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1">
                        {machine.name}
                        {machine.isUserGenerated && (
                          <Chip label="Custom" size="small" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      {isOwnProfile && (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => {
                            setSelectedMachine(machine.id);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {machine.categoryPath.join(' → ')}
                    </Typography>
                    <Typography variant="body2">{machine.metadata.manufacturer}</Typography>
                    {machine.metadata.buildVolume && (
                      <Typography variant="body2">
                        Build volume:{' '}
                        {formatMeasurementsMm([
                          machine.metadata.buildVolume.x,
                          machine.metadata.buildVolume.y,
                          machine.metadata.buildVolume.z,
                        ])}
                      </Typography>
                    )}
                    {machine.metadata.materials && machine.metadata.materials.length > 0 && (
                      <Typography variant="body2">
                        Materials: {machine.metadata.materials.join(', ')}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Stack>
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Remove Machine</DialogTitle>
        <DialogContent>
          Are you sure you want to remove this machine from your profile?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteMachine} color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
